import { useEffect, useState } from 'react';

import { BASE_PATH } from '@ai-studio/shared';

import { api } from '../../lib/api.js';

export interface InstallerInfo {
  available: boolean;
  version?: string;
  size?: number;
  updatedAt?: string;
  file?: string;
}

let cached: Promise<InstallerInfo> | null = null;

/** The desktop installer the site offers (asked once per page load). */
export function useInstaller(): InstallerInfo | null {
  const [info, setInfo] = useState<InstallerInfo | null>(null);
  useEffect(() => {
    cached ??= api.get<InstallerInfo>('/download/info').catch(() => ({ available: false }));
    void cached.then(setInfo);
  }, []);
  return info;
}

const mb = (bytes: number) => `${(bytes / 1_048_576).toFixed(0)} MB`;

export function DownloadButton({ installer, variant = 'primary' }: { installer: InstallerInfo | null; variant?: 'primary' | 'ghost' }) {
  const cls = `btn btn-lg ${variant === 'primary' ? 'btn-primary' : 'btn-ghost'}`;
  if (!installer?.available)
    return (
      <span className={`${cls} is-disabled`} aria-disabled="true">
        Windows app coming soon
      </span>
    );
  return (
    <a className={cls} href={`${BASE_PATH}/api/download/desktop`} download>
      <WindowsGlyph /> Download for Windows
    </a>
  );
}

function WindowsGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" className="btn-glyph">
      <path fill="currentColor" d="M0 2.3 6.5 1.4v6.2H0zM7.3 1.3 16 0v7.6H7.3zM0 8.4h6.5v6.2L0 13.7zM7.3 8.4H16V16l-8.7-1.2z" />
    </svg>
  );
}

const DESKTOP_EXTRAS: { title: string; text: string }[] = [
  { title: 'Your website games', text: 'Open the games you made on the website, keep building them on your PC, and sync them back.' },
  { title: 'Bring your agent', text: 'Already use Claude Code, Cursor or another AI agent? Connect it to Prism Engine Cloud and it can work on your projects too.' },
  { title: 'Native builds', text: 'Export Windows games (DirectX 12 or Vulkan), dedicated servers, and web builds right on your PC.' },
  { title: 'Same account', text: 'Sign in with your Prism Engine Cloud account: Prisma, your rank and publishing to the library come along.' },
];

/** The desktop app's download page. */
export function DownloadPage() {
  const installer = useInstaller();
  return (
    <div className="site-page download-page">
      <section className="download-hero">
        <div>
          <p className="eyebrow">Prism Engine Cloud for Windows</p>
          <h1>
            The whole engine, <span className="spectrum-text">on your machine.</span>
          </h1>
          <p className="hero-sub">
            Everything the browser editor does, plus local AI, your own CLIs and native Windows builds. Sign in once with
            the account you use here.
          </p>
          <div className="hero-ctas">
            <DownloadButton installer={installer} />
          </div>
          {installer?.available && (
            <p className="muted small download-meta">
              Version {installer.version} · {mb(installer.size ?? 0)} · Windows 10 and 11, 64-bit
              {installer.updatedAt ? ` · updated ${new Date(installer.updatedAt).toLocaleDateString()}` : ''}
            </p>
          )}
        </div>
        <div className="download-cartridge" aria-hidden="true">
          <span className="cartridge-label">PRISMCORE</span>
          <span className="cartridge-sub">GAME ENGINE</span>
          <span className="cartridge-pins" />
        </div>
      </section>
      <div className="feature-grid feature-grid-4">
        {DESKTOP_EXTRAS.map((x, i) => (
          <article key={x.title} className="feature" style={{ '--tone': ['#ffd84d', '#4ce08a', '#3fd6e8', '#a77bff'][i] } as React.CSSProperties}>
            <h3>{x.title}</h3>
            <p>{x.text}</p>
          </article>
        ))}
      </div>
      <section className="site-card install-steps">
        <h2>Installing</h2>
        <ol>
          <li>Run the installer. Windows may show a SmartScreen notice for new apps: choose “More info”, then “Run anyway”.</li>
          <li>Open Prism Engine Cloud and choose “Sign in with Prism Engine Cloud”. Approve the code in your browser.</li>
          <li>Pick a template and press Play. Local generation stays off until you set it up under Settings → Local generation.</li>
        </ol>
      </section>
    </div>
  );
}
